import matter from 'gray-matter';
import { Buffer } from 'buffer';
import {
  BLOG_POST_URLS,
  CORS_PROXY,
  CACHE_DURATION,
} from '../config/blogPosts';

// Make Buffer available globally for gray-matter
if (typeof window !== 'undefined') {
  window.Buffer = Buffer;
}

// Cache for storing processed blog posts
let blogPostsCache = null;
let cacheTimestamp = null;

/**
 * Fetches raw markdown content from URL (local or external)
 * @param {string} markdownUrl - URL to the markdown file
 * @returns {Promise<string>} - The raw markdown content
 */
const fetchRawMarkdown = async (markdownUrl) => {
  try {
    // For local files (starting with '/'), use them directly
    // For external URLs, use CORS proxy if configured
    const isLocalFile = markdownUrl.startsWith('/');
    const url = isLocalFile ? markdownUrl : CORS_PROXY + markdownUrl;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'text/plain, text/markdown, */*',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.text();
  } catch (error) {
    console.error('Error fetching markdown content:', error);
    throw new Error('Failed to fetch markdown content');
  }
};

/**
 * Parses markdown frontmatter and extracts metadata
 * @param {string} markdownContent - Raw markdown content with frontmatter
 * @param {string} markdownUrl - Original URL for fallback ID generation
 * @returns {Object} - Parsed blog post object
 */
const parseMarkdownPost = (markdownContent, markdownUrl) => {
  try {
    const { data: frontmatter, content } = matter(markdownContent);

    // Generate slug from URL filename if not provided
    const urlParts = markdownUrl.split('/');
    const filename = urlParts[urlParts.length - 1];
    const defaultSlug = filename
      .replace('.md', '')
      .replace(/[^a-z0-9-]/gi, '-')
      .toLowerCase();

    // Required fields with fallbacks
    const post = {
      id: frontmatter.slug || frontmatter.id || defaultSlug,
      title: frontmatter.title || 'Untitled Post',
      excerpt:
        frontmatter.excerpt ||
        frontmatter.description ||
        frontmatter.summary ||
        '',
      date:
        frontmatter.date ||
        frontmatter.published ||
        new Date().toISOString().split('T')[0],
      author: frontmatter.author || frontmatter.writer || 'Equipo Boton',
      thumbnail:
        frontmatter.thumbnail ||
        frontmatter.image ||
        frontmatter.cover ||
        '/Images/demo-1200w.png',
      tags: Array.isArray(frontmatter.tags)
        ? frontmatter.tags
        : frontmatter.tags
        ? frontmatter.tags.split(',').map((tag) => tag.trim())
        : [],
      category: frontmatter.category || 'General',
      featured:
        frontmatter.featured === true || frontmatter.featured === 'true',
      readingTime: frontmatter.readingTime || calculateReadingTime(content),
      markdownUrl,
      content, // Store the content without frontmatter

      // Optional SEO fields
      metaDescription:
        frontmatter.metaDescription ||
        frontmatter.excerpt ||
        frontmatter.description,
      keywords: frontmatter.keywords || frontmatter.tags,
      canonicalUrl: frontmatter.canonicalUrl,

      // Optional social fields
      socialImage:
        frontmatter.socialImage || frontmatter.thumbnail || frontmatter.image,
      twitterCard: frontmatter.twitterCard || 'summary_large_image',
    };

    // Validate required fields
    if (!post.title || !post.excerpt) {
      throw new Error(
        'Missing required frontmatter fields: title and excerpt are required'
      );
    }

    return post;
  } catch (error) {
    console.error('Error parsing markdown frontmatter:', error);
    throw new Error(`Failed to parse markdown frontmatter: ${error.message}`);
  }
};

/**
 * Generates fallback blog post when markdown parsing fails
 * @param {string} markdownUrl - The markdown URL
 * @param {string} error - Error message
 * @returns {Object} - Fallback blog post object
 */
const generateFallbackPost = (markdownUrl, error) => {
  const urlParts = markdownUrl.split('/');
  const filename = urlParts[urlParts.length - 1];
  const slug = filename
    .replace('.md', '')
    .replace(/[^a-z0-9-]/gi, '-')
    .toLowerCase();

  return {
    id: slug,
    title: `Error Loading Post: ${filename}`,
    excerpt: `Failed to load blog post from ${markdownUrl}. Error: ${error}`,
    date: new Date().toISOString().split('T')[0],
    author: 'System',
    thumbnail: '/Images/demo-1200w.png',
    tags: ['Error'],
    category: 'System',
    featured: false,
    readingTime: 1,
    markdownUrl,
    content: `# Error Loading Post\n\nFailed to load blog post from: ${markdownUrl}\n\nError: ${error}\n\nPlease check your markdown file format and frontmatter.`,
    metaDescription: `Error loading blog post: ${error}`,
    isError: true,
  };
};

/**
 * Fetches and processes a single blog post from markdown URL
 * @param {string} markdownUrl - URL to the markdown file
 * @returns {Promise<Object>} - Processed blog post object
 */
const fetchAndProcessPost = async (markdownUrl) => {
  try {
    console.log(`Fetching blog post from: ${markdownUrl}`);
    const rawMarkdown = await fetchRawMarkdown(markdownUrl);
    const post = parseMarkdownPost(rawMarkdown, markdownUrl);
    console.log(`Successfully processed post: ${post.title}`);
    return post;
  } catch (error) {
    console.error(`Error processing post from ${markdownUrl}:`, error);
    return generateFallbackPost(markdownUrl, error.message);
  }
};

/**
 * Fetches all blog posts from configured URLs
 * @returns {Promise<Array>} - Array of blog post objects
 */
export const fetchBlogPosts = async () => {
  try {
    // Check cache
    if (blogPostsCache && cacheTimestamp && CACHE_DURATION > 0) {
      const now = Date.now();
      if (now - cacheTimestamp < CACHE_DURATION) {
        console.log('Returning cached blog posts');
        return blogPostsCache;
      }
    }

    console.log('Fetching blog posts from configured URLs...');

    // Fetch all posts in parallel
    const postPromises = BLOG_POST_URLS.map((url) => fetchAndProcessPost(url));
    const posts = await Promise.all(postPromises);

    // Filter out error posts if needed and sort by date
    const validPosts = posts.filter((post) => !post.isError);
    const sortedPosts = validPosts.sort(
      (a, b) => new Date(b.date) - new Date(a.date)
    );

    // Update cache
    blogPostsCache = sortedPosts;
    cacheTimestamp = Date.now();

    console.log(`Successfully fetched ${sortedPosts.length} blog posts`);
    return sortedPosts;
  } catch (error) {
    console.error('Error fetching blog posts:', error);
    throw new Error('Failed to fetch blog posts');
  }
};

/**
 * Fetches a single blog post by slug
 * @param {string} slug - The blog post slug/ID
 * @returns {Promise<Object|null>} - Blog post object or null if not found
 */
export const fetchBlogPost = async (slug) => {
  try {
    const allPosts = await fetchBlogPosts();
    const post = allPosts.find((post) => post.id === slug);

    if (!post) {
      console.log(`Blog post not found: ${slug}`);
      return null;
    }

    return post;
  } catch (error) {
    console.error('Error fetching blog post:', error);
    throw new Error('Failed to fetch blog post');
  }
};

/**
 * Fetches markdown content for a specific post (for rendering)
 * @param {string} slug - The blog post slug/ID
 * @returns {Promise<string>} - The markdown content
 */
export const fetchMarkdownContent = async (slug) => {
  try {
    const post = await fetchBlogPost(slug);
    if (!post) {
      throw new Error('Post not found');
    }

    return post.content;
  } catch (error) {
    console.error('Error fetching markdown content:', error);
    throw new Error('Failed to fetch markdown content');
  }
};

/**
 * Fetches featured blog posts
 * @param {number} limit - Maximum number of posts to return
 * @returns {Promise<Array>} - Array of featured blog post objects
 */
export const fetchFeaturedPosts = async (limit = 3) => {
  try {
    const allPosts = await fetchBlogPosts();
    return allPosts.filter((post) => post.featured).slice(0, limit);
  } catch (error) {
    console.error('Error fetching featured posts:', error);
    throw new Error('Failed to fetch featured posts');
  }
};

/**
 * Fetches blog posts by category
 * @param {string} category - The category name
 * @returns {Promise<Array>} - Array of blog post objects in the category
 */
export const fetchPostsByCategory = async (category) => {
  try {
    const allPosts = await fetchBlogPosts();
    return allPosts.filter(
      (post) => post.category.toLowerCase() === category.toLowerCase()
    );
  } catch (error) {
    console.error('Error fetching posts by category:', error);
    throw new Error('Failed to fetch posts by category');
  }
};

/**
 * Fetches blog posts by tag
 * @param {string} tag - The tag name
 * @returns {Promise<Array>} - Array of blog post objects with the tag
 */
export const fetchPostsByTag = async (tag) => {
  try {
    const allPosts = await fetchBlogPosts();
    return allPosts.filter((post) =>
      post.tags.some((postTag) => postTag.toLowerCase() === tag.toLowerCase())
    );
  } catch (error) {
    console.error('Error fetching posts by tag:', error);
    throw new Error('Failed to fetch posts by tag');
  }
};

/**
 * Searches blog posts by title or content
 * @param {string} query - Search query
 * @returns {Promise<Array>} - Array of matching blog post objects
 */
export const searchBlogPosts = async (query) => {
  try {
    const allPosts = await fetchBlogPosts();
    const lowercaseQuery = query.toLowerCase();

    return allPosts.filter(
      (post) =>
        post.title.toLowerCase().includes(lowercaseQuery) ||
        post.excerpt.toLowerCase().includes(lowercaseQuery) ||
        post.tags.some((tag) => tag.toLowerCase().includes(lowercaseQuery)) ||
        post.content.toLowerCase().includes(lowercaseQuery)
    );
  } catch (error) {
    console.error('Error searching blog posts:', error);
    throw new Error('Failed to search blog posts');
  }
};

/**
 * Clears the blog posts cache
 */
export const clearCache = () => {
  blogPostsCache = null;
  cacheTimestamp = null;
  console.log('Blog posts cache cleared');
};

// Utility functions

/**
 * Formats a date string for display
 * @param {string} dateString - ISO date string
 * @param {string} locale - Locale for formatting (default: 'es-ES')
 * @returns {string} - Formatted date string
 */
export const formatDate = (dateString, locale = 'es-ES') => {
  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  return new Date(dateString).toLocaleDateString(locale, options);
};

/**
 * Generates SEO-friendly URL slug from title
 * @param {string} title - The blog post title
 * @returns {string} - URL-friendly slug
 */
export const generateSlug = (title) => {
  return title
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/--+/g, '-') // Replace multiple hyphens with single
    .trim('-'); // Remove leading/trailing hyphens
};

/**
 * Calculates estimated reading time for content
 * @param {string} content - The blog post content
 * @param {number} wordsPerMinute - Average reading speed (default: 200)
 * @returns {number} - Estimated reading time in minutes
 */
export const calculateReadingTime = (content, wordsPerMinute = 200) => {
  const wordCount = content.split(/\s+/).length;
  const readingTime = Math.ceil(wordCount / wordsPerMinute);
  return Math.max(1, readingTime); // Minimum 1 minute
};

/**
 * Validates markdown frontmatter
 * @param {Object} frontmatter - Parsed frontmatter object
 * @returns {Object} - Validation result with isValid and errors
 */
export const validateFrontmatter = (frontmatter) => {
  const errors = [];

  if (!frontmatter.title) {
    errors.push('Missing required field: title');
  }

  if (
    !frontmatter.excerpt &&
    !frontmatter.description &&
    !frontmatter.summary
  ) {
    errors.push('Missing required field: excerpt (or description/summary)');
  }

  if (!frontmatter.date && !frontmatter.published) {
    errors.push('Missing required field: date (or published)');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};
